import type { LoginTicket, Me } from '@regia/shared';
import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { ApiError, errorMessage, http } from '../../lib/api';
import { useAuthState } from '../../lib/auth';
import { keys } from '../../lib/query';
import { Button, Field, Input, Switch } from '../../ui';
import { AuthFrame } from './AuthFrame';
import { CodeInput } from './CodeInput';

function safeNext(next: string | null): string {
  return next?.startsWith('/') && !next.startsWith('//') ? next : '/';
}

export function LoginPage() {
  const auth = useAuthState();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const next = safeNext(params.get('next'));

  const [step, setStep] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [ticket, setTicket] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (auth.data?.user) return <Navigate to={next} replace />;
  if (auth.data?.needsSetup) return <Navigate to="/configura" replace />;

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await http.post<LoginTicket>('/api/auth/login', { email, password });
      setTicket(res.ticket);
      setStep('code');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Il codice ha 6 cifre');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const me = await http.post<Me>('/api/auth/login/code', { ticket, code, remember });
      qc.setQueryData(keys.auth, { needsSetup: false, user: me });
      navigate(next, { replace: true });
    } catch (err) {
      setCode('');
      if (err instanceof ApiError && err.status === 401 && err.code !== 'bad_code') {
        setStep('password');
        setPassword('');
      }
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'code') {
    return (
      <AuthFrame
        title="Codice di verifica"
        subtitle="Apri l’app authenticator sul telefono e scrivi il codice di Regia."
      >
        <form onSubmit={submitCode} className="flex flex-col gap-5" noValidate>
          <Field label="Codice a 6 cifre" error={error ?? undefined}>
            {(id, describedBy) => (
              <CodeInput
                id={id}
                value={code}
                onChange={setCode}
                invalid={Boolean(error)}
                describedBy={describedBy}
                autoFocus
              />
            )}
          </Field>
          <Switch
            checked={remember}
            onCheckedChange={setRemember}
            label="Ricorda questo dispositivo"
            description="Resti dentro per 30 giorni"
          />
          <Button type="submit" size="lg" block loading={busy}>
            Entra
          </Button>
          <Button variant="ghost" onClick={() => setStep('password')}>
            Torna indietro
          </Button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title="Accedi" subtitle="Entra nella tua cabina di regia.">
      <form onSubmit={submitPassword} className="flex flex-col gap-4" noValidate>
        <Field label="Email">
          {(id) => (
            <Input
              id={id}
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label="Password" error={error ?? undefined}>
          {(id, describedBy) => (
            <Input
              id={id}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              invalid={Boolean(error)}
              aria-describedby={describedBy}
              required
            />
          )}
        </Field>
        <Button type="submit" size="lg" block loading={busy} className="mt-2">
          Continua
        </Button>
      </form>
    </AuthFrame>
  );
}
